import { useAtom, useAtomValue } from "jotai";
import { useCallback } from "react";
import {
  authActionAtom,
  authUserAtom,
  isAuthenticatedAtom,
} from "@/store/auth";
import { getUserProfile } from "@/fetchers/auth";

import EVENT_ATTRIBUTE from "./event-attribute.json";
import useSourceScreen from "../use-source-screen";

/**
 * Get the device type based on screen width
 * @returns {string} Device type (desktop, tablet, or mobile)
 */
function getDeviceType() {
  if (typeof window === "undefined") return "unknown";

  const width = window.innerWidth;
  if (width >= 1024) return "desktop";
  if (width >= 768) return "tablet";
  return "mobile";
}

/**
 * Get OS details from user agent
 * @returns {string} Operating system name
 */
function getOSDetails() {
  if (typeof window === "undefined") return "unknown";

  const userAgent = window.navigator.userAgent;

  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "MacOS";
  if (/Linux/i.test(userAgent)) return "Linux";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";

  return "Other";
}

/**
 * Get browser details from user agent
 * @returns {string} Browser name
 */
function getBrowserDetails() {
  if (typeof window === "undefined") return "unknown";

  const userAgent = window.navigator.userAgent;

  if (/Chrome/i.test(userAgent) && !/Chromium|Edge|OPR|Edg/i.test(userAgent))
    return "Chrome";
  if (/Firefox/i.test(userAgent)) return "Firefox";
  if (
    /Safari/i.test(userAgent) &&
    !/Chrome|Chromium|Edge|OPR|Edg/i.test(userAgent)
  )
    return "Safari";
  if (/Edge|Edg/i.test(userAgent)) return "Edge";
  if (/OPR/i.test(userAgent)) return "Opera";
  if (/MSIE|Trident/i.test(userAgent)) return "Internet Explorer";

  return "Other";
}

/**
 * Get all UTM parameters from URL
 * @returns {Object} Object containing all UTM parameters
 */
function getAllUTMParams() {
  if (typeof window === "undefined") return {};

  const urlParams = new URLSearchParams(window.location.search);

  return {
    utmSource: urlParams.get("utm_source") || "",
    utmMedium: urlParams.get("utm_medium") || "",
    utmCampaign: urlParams.get("utm_campaign") || "",
    utmContent: urlParams.get("utm_content") || "",
    utmTerm: urlParams.get("utm_term") || "",
    gclid: urlParams.get("gclid") || "",
    campaignId: urlParams.get("campaign_id") || "",
    sourceCampaignName: urlParams.get("source_campaign_name") || "",
  };
}

/**
 * Check if MoEngage SDK is available in window context
 * @returns {boolean} - Whether MoEngage is available
 */
function isMoEngageAvailable() {
  return (
    typeof window !== "undefined" &&
    window.Moengage &&
    typeof window.Moengage.track_event === "function"
  );
}

/**
 * Standalone function to track MoEngage events
 * @param {string} event - Event name to track
 * @param {Object} attributes - Event attributes
 */
export const trackMoengageEvent = async (event, attributes) => {
  try {
    const timer = setInterval(async () => {
      if (!isMoEngageAvailable()) return;

      // Clear Interval
      clearInterval(timer);

      // Ensure attributes is an object
      attributes = attributes || {};

      // Add platform information
      attributes[EVENT_ATTRIBUTE.PLATFORM] = "web";
      attributes[EVENT_ATTRIBUTE.OS_VERSION] = getOSDetails();
      attributes[EVENT_ATTRIBUTE.BROWSER_DETAILS] = getBrowserDetails();
      attributes[EVENT_ATTRIBUTE.DEVICE_TYPE] = getDeviceType();

      // Add UTM parameters if available
      const utmParams = getAllUTMParams();
      if (utmParams.utmSource) {
        attributes[EVENT_ATTRIBUTE.UTM_SOURCE] = utmParams.utmSource;
      }
      if (utmParams.utmMedium) {
        attributes[EVENT_ATTRIBUTE.UTM_MEDIUM] = utmParams.utmMedium;
      }
      if (utmParams.utmCampaign) {
        attributes[EVENT_ATTRIBUTE.UTM_CAMPAIGN] = utmParams.utmCampaign;
      }
      if (utmParams.utmContent) {
        attributes[EVENT_ATTRIBUTE.UTM_CONTENT] = utmParams.utmContent;
      }
      if (utmParams.utmTerm) {
        attributes[EVENT_ATTRIBUTE.UTM_TERM] = utmParams.utmTerm;
      }
      if (utmParams.gclid) {
        attributes[EVENT_ATTRIBUTE.GCLID] = utmParams.gclid;
      }
      if (utmParams.campaignId) {
        attributes[EVENT_ATTRIBUTE.CAMPAIGN_ID] = utmParams.campaignId;
      }
      if (utmParams.sourceCampaignName) {
        attributes[EVENT_ATTRIBUTE.SOURCE_CAMPAIGN_NAME] =
          utmParams.sourceCampaignName;
      }

      // Remove null/undefined values
      const cleanAttributes = Object.keys(attributes).reduce((acc, key) => {
        const newObject = acc;
        if (attributes[key] !== undefined) newObject[key] = attributes[key];
        return newObject;
      }, {});

      console.log("MoEngage Event Tracked:", event, cleanAttributes);

      // Track the event
      window.Moengage.track_event(event, cleanAttributes);
    }, 100);
  } catch (error) {
    console.error("Failed to track MoEngage event:", event, error);
  }
};

/**
 * Custom React hook for tracking MoEngage events with user context
 * @param {Object} defaultAttrs - Default attributes to include with all events
 * @returns {Function} trackEvent - Function to track MoEngage events
 */
export const useMoengageEventTracker = (defaultAttrs = {}) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const authAction = useAtomValue(authActionAtom);

  const [authUser, setAuthUser] = useAtom(authUserAtom);

  const sourceScreen = useSourceScreen();

  return useCallback(
    async (event, attributes = {}) => {
      try {
        // Add user information if authenticated
        if (isAuthenticated && authUser) {
          attributes.user_id = authUser.userId || authUser.id;
          attributes.user_type = authUser.userType;
          attributes.user_email = authUser.email;
          attributes.user_mobile = authUser.mobileNumber;
          attributes.expired_count = authUser.expiredCount;
          attributes.user_name = authUser.firstName;
          attributes.active_count = authUser.activeCount;
          attributes.is_paid_user = authUser.paidStatus ? "yes" : "no";
          attributes.user_state = authUser.state;
          attributes.user_city = authUser.city;

          // If user profile is not complete, fetch it
        } else if (isAuthenticated && !authUser) {
          const profile = await getUserProfile();
          setAuthUser(profile);

          if (profile) {
            attributes.user_id = profile.userId || profile.id;
            attributes.user_type = profile.userType;
            attributes.user_email = profile.email;
            attributes.user_name = profile.firstName;
            attributes.user_mobile = profile.mobileNumber;
            attributes.expired_count = profile.expiredCount;
            attributes.active_count = profile.activeCount;
            attributes.user_name = profile.firstName;
            attributes.is_paid_user = profile.paidStatus ? "yes" : "no";
            attributes.user_state = profile.state;
            attributes.user_city = profile.city;
          }
        }

        if (attributes.is_paid_user) {
          window.Moengage.add_user_attribute(
            "paid_Status",
            attributes.is_paid_user === "yes" ? 1 : 0
          );
        }

        // Set user type based on authentication status
        attributes.user_type =
          attributes.user_id || authAction === 1 ? "registered" : "guest";

        // Track the event with combined attributes
        trackMoengageEvent(
          event,
          { source_screen: sourceScreen, ...defaultAttrs, ...attributes },
          isAuthenticated
        );
      } catch (error) {
        console.error(
          "Failed to track MoEngage event with user context:",
          error
        );
      }
    },
    [
      authAction,
      authUser,
      setAuthUser,
      isAuthenticated,
      defaultAttrs,
      sourceScreen,
    ]
  );
};

export default useMoengageEventTracker;
